import { useFormContext } from "react-hook-form"
import { AllSchemas } from "../schema"
import { Input } from '../CreditBalanceForm/Input'
import { zipCodeMask } from "@/app/utils/zipCodeFormater"
import { banks } from "@/app/utils/bank"
import { Summary } from "./summary"
import { Button } from "@/view/components/ui/button"
import { Landmark, Mail, MapPin } from "lucide-react"
import { Select } from "./select"
import { CreditCard } from "@/view/components/CreditCard"
import { useFinalizationForm } from "./useFinalizationForm"
import { orgs } from "@/app/utils/orgs"
import { uf } from "@/app/utils/uf"
import { rgMask } from "@/app/utils/rgNumberFormater"
import { dateMask } from "@/app/utils/dateFormater"
import { states } from "@/app/utils/states"
import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext"
import { PartnerType } from "@/app/utils/partners.enum"
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners"
import { CreditPartner } from "../../useCreditController"
import { cn } from "@/app/utils/cn"

export const FinalizationForm = () => {
  const methods = useFormContext<AllSchemas>()
  const { handleSubmit, isLoading, banksPan, handleCepRequest } = useFinalizationForm(methods)
  const { type } = useSelfHiringContext()

  return (
    <div className="flex flex-col gap-4 w-full">

      <div
        className="flex mx-auto mt-2 h-[72px] w-full shrink-0 items-center justify-center rounded-xl"
        style={{ backgroundColor: getPartnerBgColor(type as PartnerType) }}
      >
        <img
          src={getPartnerImage(type as CreditPartner)}
          alt={(type as string) ?? ""}
          className={cn("h-14 object-contain", type === PartnerType.PAN && "h-20")}
        />
      </div>

      <Summary />

      <CreditCard>
        <h1 className="text-black01 text-lg md:text-2xl font-bold">
          Finalize seu Empréstimo
        </h1>
        <p className="text-xs mb-4">Preencha os dados finais para finalizar seu empréstimo</p>
        <div className="flex flex-col gap-3 w-full">
          <Input
            name="email"
            label="E-mail"
            icon={<Mail className="mr-1 h-4 w-4 text-black03" />}
            error={methods.formState.errors.email?.message}
            placeholder="Insira seu e-mail"
          />

          <div className="grid grid-cols-1 md:grid-cols-[60%_40%] gap-3">
            <Input
              label="RG"
              name="rg.number"
              type="text"
              onInput={rgMask}
              maxLength={9}
              error={methods.formState.errors.rg?.number?.message}
            />
            <Select
              label="UF de emissão"
              name="rg.uf"
              options={uf}
              placeholder="Selecione"
              error={methods.formState.errors.rg?.uf?.message}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[60%_40%] gap-3">
            <Select
              label="Orgão de emissão"
              name="rg.org"
              options={orgs}
              placeholder="Selecione o Órgão de emissão"
              error={methods.formState.errors.rg?.org?.message}
            />
            <Input
              label="Data de emissão"
              name="rg.date"
              type="text"
              inputMode="numeric"
              maxLength={10}
              onInput={dateMask}
              placeholder="dd/mm/aaaa"
              error={methods.formState.errors.rg?.date?.message}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2">
            <Input
              name="address.zipCode"
              disabled={type === PartnerType.CREFAZ}
              label="CEP"
              onBlur={(e) => handleCepRequest(e.target.value)}
              maxLength={9}
              icon={<MapPin className="" height={15} width={16} />}
              error={methods.formState.errors.address?.zipCode?.message}
              placeholder="Insira seu CEP"
              onInput={zipCodeMask}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[50%_50%] gap-3 w-full">
            <Input
              name="address.street"
              label="Endereço"
              error={methods.formState.errors.address?.street?.message}
              placeholder="Insira seu endereço"
            />
            <Input
              className="flex-1"
              name="address.number"
              label="Número"
              error={methods.formState.errors.address?.number?.message}
              placeholder=""
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[50%_50%] gap-3 w-full">
            <Input
              className="flex-1"
              name="address.complement"
              label="Complemento"
              error={methods.formState.errors.address?.complement?.message}
              placeholder="Insira seu complemento"
            />
            <Input
              className="flex-1"
              name="address.neighborhood"
              label="Bairro"
              error={methods.formState.errors.address?.neighborhood?.message}
              placeholder="Insira seu bairro"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[50%_50%] gap-3 items-center">
            <Input
              label="Cidade"
              name="address.city"
              type="text"
              error={methods.formState.errors.address?.city?.message}
            />
            <Select
              label="Estado"
              name="address.state"
              options={states}
              placeholder="Selecione o seu estado"
              error={methods.formState.errors.address?.state?.message}
            />
          </div>

          {type === PartnerType.CREFAZ && (
            <div className="grid grid-cols-1 md:grid-cols-[50%_50%] gap-3 items-center">
              <Input
                label="Cidade de naturalidade"
                name="placeOfBirth.city"
                type="text"
                error={methods.formState.errors.placeOfBirth?.city?.message}
              />
              <Select
                label="UF de naturalidade"
                name="placeOfBirth.uf"
                options={uf}
                placeholder="Selecione"
                error={methods.formState.errors.placeOfBirth?.uf?.message}
              />
            </div>
          )}

          <hr className="w-full bg-primary h-[3px] my-2" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
            <Select
              name="bankAccount.bank"
              label="Banco"
              labelIcon={<Landmark width={14} height={16} className="mr-1 text-black03 fill-black03" />}
              options={type === PartnerType.PAN ? banksPan : banks}
              placeholder="Selecione"
              error={methods.formState.errors.bankAccount?.bank?.message}
            />
            <Input
              name="bankAccount.agency"
              label="Agência"
              error={methods.formState.errors.bankAccount?.agency?.message}
              placeholder="Insira sua agência"
              inputMode="numeric"
              maxLength={4}
            />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
            <Select
              name="bankAccount.accountType"
              label="Tipo de conta"
              options={[{ label: 'Conta Corrente', value: 'CC' }, { label: 'Conta Poupança', value: 'CP' }]}
              placeholder="Selecione "
              error={methods.formState.errors.bankAccount?.accountType?.message}
            />
            <Input
              name="bankAccount.account"
              label="Conta C/ Digito"
              error={methods.formState.errors.bankAccount?.account?.message}
              placeholder="Insira sua conta"
              inputMode="numeric"
              maxLength={11}
            />

          </div>

          <Button
            isLoading={isLoading}
            onClick={handleSubmit}
            className="text-white w-max ml-auto px-14 font-semibold rounded-xl mt-2"
          >
            Finalizar
          </Button>
        </div>
      </CreditCard>
    </div>
  )
}